/* import __COLOCATED_TEMPLATE__ from './gift-card-limit-config.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import ajax from 'embercom/lib/ajax';
import type ShopifyIntegration from 'embercom/models/shopify/integration';

interface GiftCardLimitConfigSignature {
  Args: {
    shops: ShopifyIntegration[];
  };
}

export default class GiftCardLimitConfig extends Component<GiftCardLimitConfigSignature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;

  @tracked editingValues: Record<string, string> = {};
  @tracked savingShopId: string | null = null;

  get displayValues(): Record<string, string> {
    let values: Record<string, string> = {};
    for (let shop of this.args.shops) {
      if (shop.id in this.editingValues) {
        values[shop.id] = this.editingValues[shop.id]!;
      } else if (shop.giftCardLimitPercentage != null) {
        values[shop.id] = String(Math.round(shop.giftCardLimitPercentage * 100));
      } else {
        values[shop.id] = '';
      }
    }
    return values;
  }

  get isSaving(): boolean {
    return this.savingShopId !== null;
  }

  get isEditing(): Record<string, boolean> {
    let result: Record<string, boolean> = {};
    for (let shopId of Object.keys(this.editingValues)) {
      result[shopId] = true;
    }
    return result;
  }

  @action
  updateValue(shopId: string, event: Event) {
    let target = event.target as HTMLInputElement;
    this.editingValues = { ...this.editingValues, [shopId]: target.value };
  }

  @action
  saveLimit(shop: ShopifyIntegration) {
    void taskFor(this.saveLimitTask).perform(shop);
  }

  @dropTask
  *saveLimitTask(shop: ShopifyIntegration): TaskGenerator<void> {
    this.savingShopId = shop.id;
    let rawValue = this.editingValues[shop.id] ?? '';
    let trimmed = rawValue.trim();

    let limitPercentage: number | null = null;
    if (trimmed !== '') {
      let parsed = parseFloat(trimmed);
      if (isNaN(parsed) || parsed <= 0) {
        this.notificationsService.notifyError(
          this.intl.t('appstore.settings.shopify.gift-card-limit.validation.must-be-positive'),
        );
        this.savingShopId = null;
        return;
      }
      limitPercentage = parsed / 100;
    }

    try {
      let response: { gift_card_limit_percentage: number | null } = yield ajax({
        type: 'PUT',
        url: `/ember/shopify/integrations/${shop.id}/gift_card_limits?app_id=${this.appService.app.id}`,
        data: JSON.stringify({ gift_card_limit_percentage: limitPercentage }),
      });

      shop.set('giftCardLimitPercentage', response.gift_card_limit_percentage);

      let { [shop.id]: _, ...rest } = this.editingValues;
      this.editingValues = rest;

      this.notificationsService.notifyConfirmation(
        this.intl.t('appstore.settings.shopify.gift-card-limit.save-success'),
      );
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('appstore.settings.shopify.gift-card-limit.save-failure'),
      });
    } finally {
      this.savingShopId = null;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Appstore::AppPackage::Settings::Shopify::GiftCardLimitConfig': typeof GiftCardLimitConfig;
    'appstore/app-package/settings/shopify/gift-card-limit-config': typeof GiftCardLimitConfig;
  }
}
